/**
 * The sidebar's navigation, in one place, so later phases add a page by
 * swapping its `element` in routes.tsx and never by editing the chrome.
 *
 * `phase` is the build phase that fills the page in (see the build plan); a
 * page whose phase has not landed renders the placeholder.
 *
 * The navigation is SECTIONED, not flat. A creator's sidebar answers three
 * different questions — what can I work on, what have I done, and what is mine
 * — and an operator's answers three others. Fifteen links in one column is a
 * list to read; five groups of three is a place to look.
 *
 * There is exactly ONE nav per person: a creator sees the creator sections, an
 * operator sees the operator sections, and nobody sees both. A single account
 * that is both does not exist in v1, so there is no switcher.
 */
export type NavIconName =
  | 'campaigns'
  | 'submit'
  | 'submissions'
  | 'leaderboard'
  | 'connections'
  | 'wallet'
  | 'referrals'
  | 'profile'
  | 'overview'
  | 'review'
  | 'creators'
  | 'invitations'
  | 'payouts'
  | 'ledger'
  | 'reports'
  | 'users'
  | 'outbox'
  | 'support'
  | 'settings';

export interface NavItem {
  path: string;
  label: string;
  phase: number;
  icon: NavIconName;
}

export interface NavSection {
  /** Small uppercase label above the group. */
  label: string;
  items: NavItem[];
}

/** Where a creator's sidebar starts, and where signing in sends them. */
export const CREATOR_HOME = '/campaigns';

/** Where an operator's sidebar starts, and where signing in sends them. */
export const OPERATOR_HOME = '/admin';

/** A creator's navigation, grouped by the question it answers. */
export const CREATOR_SECTIONS: NavSection[] = [
  {
    label: 'Campaigns',
    items: [{ path: '/campaigns', label: 'Campaigns', phase: 1, icon: 'campaigns' }],
  },
  {
    label: 'Your work',
    items: [
      { path: '/submit', label: 'Submit', phase: 3, icon: 'submit' },
      { path: '/submissions', label: 'My submissions', phase: 3, icon: 'submissions' },
      { path: '/leaderboard', label: 'Leaderboard', phase: 4, icon: 'leaderboard' },
    ],
  },
  {
    label: 'Account',
    items: [
      { path: '/connections', label: 'Connections', phase: 2, icon: 'connections' },
      { path: '/wallet', label: 'Wallet', phase: 5, icon: 'wallet' },
      /* Under Account and not under "Your work": a referral is something you
         own, like your wallet and your connections, rather than something you
         do for a campaign. Creator-only — `CREATOR_SECTIONS` is the only nav an
         operator never sees, and the operator's view of the same programme is
         /admin/referrals. */
      { path: '/referrals', label: 'Referrals', phase: 7, icon: 'referrals' },
      { path: '/profile', label: 'Profile', phase: 2, icon: 'profile' },
      /* Last under Account: the door to a person, for when the screens above
         have not answered the question. The shell puts an unread count on it. */
      { path: '/support', label: 'Support', phase: 11, icon: 'support' },
    ],
  },
];

/**
 * An operator's navigation.
 *
 * Hidden from everybody else — a courtesy, not a control. The routes still
 * exist in this (public) bundle, and what actually refuses a creator is the API
 * returning 403; `RequireAdmin` only decides what to render.
 */
export const OPERATOR_SECTIONS: NavSection[] = [
  {
    label: 'Operate',
    items: [
      { path: '/admin', label: 'Overview', phase: 0, icon: 'overview' },
      { path: '/admin/review', label: 'Review', phase: 3, icon: 'review' },
      /* Beside Review, because it is the same kind of queue: a person waiting
         on the operator. The shell puts the count of tickets needing a reply
         on it. */
      { path: '/admin/support', label: 'Support', phase: 11, icon: 'support' },
      { path: '/admin/campaigns', label: 'Campaigns', phase: 1, icon: 'campaigns' },
      { path: '/admin/creators', label: 'Creators', phase: 2, icon: 'creators' },
      /* Under Operate and not under Money, though half of what it reports is
         money: it is the screen an operator opens to ask "how are we doing",
         which is the same question the review queue and the campaign list
         answer one campaign at a time. */
      { path: '/admin/reports', label: 'Reports', phase: 9, icon: 'reports' },
      { path: '/admin/invitations', label: 'Invitations', phase: 2, icon: 'invitations' },
    ],
  },
  {
    label: 'Money',
    items: [
      { path: '/admin/payouts', label: 'Payouts', phase: 5, icon: 'payouts' },
      { path: '/admin/ledger', label: 'Ledger', phase: 5, icon: 'ledger' },
      { path: '/admin/referrals', label: 'Referrals', phase: 7, icon: 'referrals' },
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/admin/users', label: 'Users', phase: 1, icon: 'users' },
      { path: '/admin/outbox', label: 'Outbox', phase: 1, icon: 'outbox' },
      /* Last under System: every configurable, on one page, saved from here
         rather than from a deploy. */
      { path: '/admin/settings', label: 'Settings', phase: 12, icon: 'settings' },
    ],
  },
];

/** Every creator page, flat, for building routes. */
export const CREATOR_NAV: NavItem[] = CREATOR_SECTIONS.flatMap((section) => section.items);

/** Every operator page, flat, for building routes. */
export const ADMIN_NAV: NavItem[] = OPERATOR_SECTIONS.flatMap((section) => section.items);

/** The sections one person sees. Never both. */
export function sectionsFor(isAdmin: boolean): NavSection[] {
  return isAdmin ? OPERATOR_SECTIONS : CREATOR_SECTIONS;
}

/** Where signing in lands somebody. */
export function homeFor(isAdmin: boolean): string {
  return isAdmin ? OPERATOR_HOME : CREATOR_HOME;
}

/**
 * The nav label for an address, for the topbar before the page has said what it
 * is. Longest match wins, so `/admin/campaigns/jag-10m` reads "Campaigns" and
 * not "Overview".
 */
export function navLabelFor(pathname: string): string | undefined {
  let best: NavItem | undefined;
  for (const item of [...CREATOR_NAV, ...ADMIN_NAV]) {
    const hit = pathname === item.path || pathname.startsWith(`${item.path}/`);
    if (hit && (!best || item.path.length > best.path.length)) best = item;
  }
  return best?.label;
}
